import { InputStatus } from 'de-zurich-ui-library'

import { isValidVSNR } from '@/utilsTenant/rules'
import type { ErrorResponse } from '@/types/ServiceResponse'

export function checkIfEmailInputIsValid(email: string | undefined): InputStatus {
  if (email) {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email) ? InputStatus.VALID : InputStatus.ERROR
  } else {
    return InputStatus.ERROR
  }
}

export function checkIfVsnrInputIsValid(vsnr: string | undefined): InputStatus {
  if (vsnr) {
    return isValidVSNR(vsnr) ? InputStatus.VALID : InputStatus.ERROR
  } else {
    return InputStatus.ERROR
  }
}

export function checkIfPostcodeInputIsValid(postcode: string | undefined): InputStatus {
  if (postcode) {
    const postcodeRegex = /^[a-zA-Z0-9 ./-]{3,6}$/
    return postcodeRegex.test(postcode) ? InputStatus.VALID : InputStatus.ERROR
  } else {
    return InputStatus.ERROR
  }
}

export function checkIfBirthdayInputIsValid(birthday: string | undefined): InputStatus {
  if (birthday == '') {
    return InputStatus.DEFAULT
  } else if (birthday) {
    // Format muss dd.mm.yyyy entsprechen (z.B. 01.12.1990)
    const birthdayRegex = /^(\d{2})\.(\d{2})\.(\d{4})$/
    const match = birthdayRegex.exec(birthday)

    if (!match) {
      return InputStatus.ERROR
    }

    const day = Number(match[1])
    const month = Number(match[2])
    const year = Number(match[3])

    // Monat muss zwischen 1 und 12 liegen
    if (month < 1 || month > 12) {
      return InputStatus.ERROR
    }

    // Tag muss zwischen 1 und 31 liegen
    if (day < 1 || day > 31) {
      return InputStatus.ERROR
    }

    // Datum darf nicht in der Zukunft liegen
    const inputDate = new Date(year, month - 1, day)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Tag/Monat/Jahr-Kombination muss real existieren (erkennt z.B. 30.02.
    // oder 29.02. in Nicht-Schaltjahren, da JS Date sonst in den nächsten
    // Monat überläuft statt einen Fehler zu liefern)
    if (
      inputDate.getFullYear() !== year ||
      inputDate.getMonth() !== month - 1 ||
      inputDate.getDate() !== day
    ) {
      return InputStatus.ERROR
    }

    if (inputDate > today) {
      return InputStatus.ERROR
    }

    return InputStatus.VALID
  } else {
    return InputStatus.ERROR
  }
}

export function is5xxError(status: number): boolean {
  return status >= 500 && status < 600
}
export function is4xxError(status: number): boolean {
  return status >= 400 && status < 500
}

export function isError400UserExisting(error: Response): Promise<boolean> {
  return error.json().then((value) => {
    const err = value as ErrorResponse
    return err?.code === 'E400-009-AMS'
  })
}

/**
 * Addiert Jahre zu einem Datum nach Java `LocalDate.plusYears`-Semantik:
 * Fällt das Zieldatum auf einen Tag, der im Zielmonat nicht existiert
 * (z.B. 29.02. in einem Nicht-Schaltjahr), wird auf den letzten gültigen
 * Tag des Zielmonats geklemmt (z.B. 28.02.), statt (wie beim nativen
 * JS-`Date`-Verhalten) in den nächsten Monat überzulaufen.
 */
export function addYearsJavaStyle(date: Date, years: number): Date {
  const targetYear = date.getFullYear() + years
  const month = date.getMonth()
  const day = date.getDate()
  const daysInTargetMonth = new Date(targetYear, month + 1, 0).getDate()
  const clampedDay = Math.min(day, daysInTargetMonth)

  return new Date(targetYear, month, clampedDay)
}

/**
 * Prüft, ob eine Person am aktuellen Tag (lokale Zeitzone) ein Alter im
 * Bereich [minAgeInclusive, maxAgeExclusive) hat.
 *
 * - Der untere Grenzwert ist inklusiv: Der Geburtstag, an dem `minAgeInclusive`
 *   erreicht wird, gilt bereits als gültig.
 * - Der obere Grenzwert ist exklusiv: Am Geburtstag, an dem `maxAgeExclusive`
 *   erreicht wird, gilt die Person bereits als zu alt.
 * - Schaltjahr-Geburtstage (29.02.) werden nach Java-`plusYears`-Semantik
 *   behandelt (siehe `addYearsJavaStyle`).
 * - Es wird ausschliesslich mit lokalen Datumsanteilen (Jahr/Monat/Tag)
 *   gerechnet, nicht mit UTC-Zeitstempeln, damit Zeitzonen-Randfälle
 *   korrekt anhand des lokalen Kalendertags entschieden werden.
 *
 * @param dateOfBirth Geburtsdatum im Format dd.mm.yyyy
 * @param minAgeInclusive unteres Altersgrenze (inklusiv)
 * @param maxAgeExclusive obere Altersgrenze (exklusiv)
 */
export function isAgeBetween(
  dateOfBirth: string | undefined,
  minAgeInclusive: number,
  maxAgeExclusive: number
): boolean {
  if (!dateOfBirth || checkIfBirthdayInputIsValid(dateOfBirth) === InputStatus.ERROR) {
    return false
  }

  const [day, month, year] = dateOfBirth.split('.').map(Number)
  const birthDate = new Date(year!, month! - 1, day)

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  const lowerBoundDate = addYearsJavaStyle(birthDate, minAgeInclusive)
  const upperBoundDate = addYearsJavaStyle(birthDate, maxAgeExclusive)

  return today >= lowerBoundDate && today < upperBoundDate
}
